import { useState, useEffect } from 'react';
import banner01 from '../../../assets/banner01.png';
import { X } from 'lucide-react';
import featureImg01 from '../../../assets/ai_feature01.png';
import featureImg02 from '../../../assets/ai_feature02.png';
import featureImg03 from '../../../assets/ai_feature03.png';
import featureImg04 from '../../../assets/ai_feature04.png';
import featureImg05 from '../../../assets/ai_feature05.png';
import featureImg06 from '../../../assets/ai_feature06.png';
import Portal from '../../../components/common/Portal';

const FeaturesSection = () => {
  const [selectedVideo, setSelectedVideo] = useState<string | null>(null);

  const features = [
    {
      icon: 'ri-robot-line',
      title: 'AI탐험여권',
      description:
        '내 여권에 도장이 하나씩 늘어날 때마다 진짜 탐험가가 되어가요! AI와 함께하는 나만의 성장 기록!',
      image: featureImg01,
      modalVideo: 'https://player.vimeo.com/video/1195852547'
    },
    {
      icon: 'ri-live-line',
      title: 'AI체험여행',
      description:
        '우리 아이가 만든 이야기가 진짜 동화책이 된다면? AI로 동화책도 만들고 노래도 만드는 신기한 체험, 아이와 함께 즐겨 보세요!',
      image: featureImg02,
      modalVideo: 'https://player.vimeo.com/video/1195852615'
    },
    {
      icon: 'ri-gamepad-line',
      title: 'AI놀이여행',
      description:
        'AI와 함께라면 매일매일 새로운 놀이가 기다리는 AI놀이여행 출발!',
      image: featureImg03,
      modalVideo: 'https://player.vimeo.com/video/1195852614'
    },
    {
      icon: 'ri-group-line',
      title: 'AI기초여행',
      description:
        "AI는 어떻게 그림도, 이야기도, 노래도 척척 만들어 낼까요? 그 비밀의 열쇠는 다름 아닌 '글쓰기'예요. 함께 써볼까요?",
      image: featureImg04,
      modalVideo: 'https://player.vimeo.com/video/1195852549'
    },
    {
      icon: 'ri-trophy-line',
      title: 'AI응용여행',
      description:
        '아이의 손끝에서 탄생하는 다양한 AI 작품들, 체험을 거듭할수록 우리 아이만의 특별한 포트폴리오가 쌓여 가요.',
      image: featureImg05,
      modalVideo: 'https://player.vimeo.com/video/1195852546'
    },
    {
      icon: 'ri-links-line',
      title: 'AI프로젝트여행',
      description:
        '우리 아이의 놀라운 작품을 직접 만나 보세요! AI 웹툰, 뮤직비디오, 애니메이션, 음악까지 상상이 현실이 되는 특별한 시간이에요.',
      image: featureImg06,
      modalVideo: 'https://player.vimeo.com/video/1195852548'
    }
  ];

  useEffect(() => {
    if (selectedVideo) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [selectedVideo]);

  return (
    <section id="features" className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6">
            아이노우 라이브 클래스만의{' '}
            <span className="text-blue-600">특별함</span>
          </h2>
          <p className="md:text-xl text-lg text-gray-600 max-w-3xl mx-auto">
            최신 AI 기술과 전문 튜터가 함께하는 혁신적인 체험을 만나 보세요.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {features.map((feature, index) => (
            <div
              key={index}
              className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2"
              onClick={() => setSelectedVideo(feature.modalVideo)}
            >
              <div className="mb-6">
                <img
                  src={feature.image}
                  alt={feature.title}
                  className="w-full h-48 object-cover rounded-xl mb-4 object-top"
                />
                <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center mx-auto">
                  <i className={`${feature.icon} text-2xl text-white`}></i>
                </div>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">
                {feature.title}
              </h3>
              <p className="text-gray-600 text-center leading-relaxed">
                {feature.description}
              </p>
            </div>
          ))}
        </div>
        <div className="text-center w-full mt-8 md:w-3/5 md:mx-auto md:mt-16">
          <a
            href="http://aiknow.ai.kr/main/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block"
          >
            <img src={banner01} alt="" />
          </a>
        </div>
      </div>
      <Portal>
        {selectedVideo && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
            onClick={() => setSelectedVideo(null)}
          >
            <div
              className="relative w-full max-w-[420px] max-h-[90vh] aspect-[9/16] bg-black"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setSelectedVideo(null)}
                className="absolute top-[-40px] right-0 z-10 bg-black/60 hover:bg-black/80 text-white rounded-full p-2 transition-colors"
              >
                <X size={20} />
              </button>

              <iframe
                src={`${selectedVideo}${selectedVideo.includes('?') ? '&' : '?'}autoplay=1`}
                title="Vimeo Video"
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
                className="absolute inset-0 w-full h-full"
              />
            </div>
          </div>
        )}
      </Portal>
    </section>
  );
};

export default FeaturesSection;
